import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { historyService } from '@/lib/api/services/history'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  History,
  CreateHistory,
  UpdateHistory,
  HistoryFilterParams,
  UpdateHistoryStatus,
  HistoryStats
} from '@/lib/api/types'

// Query keys for caching
export const historyKeys = {
  all: ['histories'] as const,
  lists: () => [...historyKeys.all, 'list'] as const,
  list: (filters?: HistoryFilterParams) => [...historyKeys.lists(), filters] as const,
  details: () => [...historyKeys.all, 'detail'] as const,
  detail: (id: string) => [...historyKeys.details(), id] as const,
  stats: () => [...historyKeys.all, 'stats'] as const,
  filtered: (params: PaginationParams, filters: HistoryFilterParams) =>
    [...historyKeys.all, 'filtered', params, filters] as const,
  search: (query: string, params: PaginationParams) => [...historyKeys.all, 'search', query, params] as const
}

// Hook to fetch history stats
export function useHistoryStats() {
  return useQuery<HistoryStats, Error>({
    queryKey: historyKeys.stats(),
    queryFn: async () => {
      const response = await historyService.getHistoryStats()
      return response.data
    }
  })
}

// Hook to filter histories with pagination
export function useFilterHistories(
  params: PaginationParams = { page: 1, size: 10 },
  filters: HistoryFilterParams = {},
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<History>>, Error>({
    queryKey: historyKeys.filtered(params, filters),
    queryFn: () => historyService.filterHistories(params, filters),
    enabled: enabled
  })
}

// Hook to fetch a single history
export function useHistory(historyId?: string) {
  return useQuery<ApiResponse<History>, Error>({
    queryKey: historyKeys.detail(historyId!),
    queryFn: () => historyService.getHistoryById(historyId!),
    enabled: !!historyId
  })
}

// Hook to create a history
export function useCreateHistory() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<History>, Error, CreateHistory>({
    mutationFn: (data) => historyService.createHistory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: historyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: historyKeys.all })
      queryClient.invalidateQueries({ queryKey: historyKeys.stats() })
    }
  })
}

// Hook to update a history
export function useUpdateHistory() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<History>,
    Error,
    { historyId: string; data: UpdateHistory }
  >({
    mutationFn: ({ historyId, data }) =>
      historyService.updateHistory(historyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: historyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: historyKeys.all })
      queryClient.invalidateQueries({ queryKey: historyKeys.detail(variables.historyId) })
      queryClient.invalidateQueries({ queryKey: historyKeys.stats() })
    }
  })
}

// Hook to delete a history
export function useDeleteHistory() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { historyId: string; data: UpdateHistory }
  >({
    mutationFn: ({ historyId }) =>
      historyService.deleteHistory(historyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: historyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: historyKeys.all })
      queryClient.invalidateQueries({ queryKey: historyKeys.stats() })
    }
  })
}

// Hook to search histories
export function useSearchHistories(
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<History>>, Error>({
    queryKey: historyKeys.search(searchBy || '', params),
    queryFn: () => historyService.searchHistories(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to update history status
export function useUpdateHistoryStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<History>,
    Error,
    { historyId: string; data: UpdateHistoryStatus }
  >({
    mutationFn: ({ historyId, data }) =>
      historyService.updateHistoryStatus(historyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: historyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: historyKeys.all })
      queryClient.invalidateQueries({ queryKey: historyKeys.detail(variables.historyId) })
      queryClient.invalidateQueries({ queryKey: historyKeys.stats() })
    }
  })
}

